import { useMutation } from 'react-apollo';
import pick from 'lodash.pick';
import omit from 'lodash.omit';
import produce from 'immer';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import { cardElementCreate as cardElementCreateMutation } from '../../../apps/cards/graphql/mutations/cardElement';
import { cardTemplateUpdate as cardTemplateUpdateMutation } from '../../../apps/cards/graphql/mutations/cardTemplate';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import { getCardDesignerCardElementConfigurationInputByElement } from './helpers';
import cardDetails from '../../../services/cards/graphql/fragments/cardDetails';
import { cardElementConfigurationCreate as cardElementConfigurationCreateMutation } from '../../../apps/cards/graphql/mutations/cardElementConfiguration';
import { CARD_ELEMENT_TYPE } from '../../../services/cards/constants';
import { PHOTO_TYPES } from '../../../constants/photo-types';

const useCardElementCreate = ({
  card,
  cardSide,
  cardTemplate,
  setSelectedCardElementId,
} = {}) => {
  const [cardElementCreate] = useMutation(cardElementCreateMutation);
  const [cardTemplateUpdate] = useMutation(cardTemplateUpdateMutation);
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );
  const openAlert = useOpenAlert();

  const onCardElementCreate = async ({
    input,
    cardElementType,
    cardElementConfiguration,
  }) => {
    try {
      const cardElementCreateResponse = await cardElementCreate({
        variables: {
          input: {
            type: cardElementType,
            z_index: 1,
            ...input,
          },
        },
      });
      const cardElement =
        cardElementCreateResponse.data.cardElementCreate.card_element;

      // Add configuration before adding the item to the template
      // this ensures that when the item appears, it is actually configured
      if (cardElementConfiguration) {
        const configFields = [
          'border',
          'corner_radius',
          'is_editable',
          'is_printable',
          'required_properties',
        ];

        if (cardElementType !== CARD_ELEMENT_TYPE.TEXT) {
          configFields.push('help_placeholder');
        }

        const config = pick(cardElementConfiguration, configFields);
        const elementConfig = omit(cardElementConfiguration, configFields);

        try {
          await cardElementConfigurationCreate({
            variables: {
              input: {
                card_element_id: cardElement.id,
                card_side_id: cardSide.id,
                ...config,
                ...getCardDesignerCardElementConfigurationInputByElement(
                  { cardElement },
                  elementConfig,
                  config.is_editable,
                ),
              },
            },
            update: (cache, { data }) => {
              const cachedCard = cache.readFragment({
                id: defaultDataIdFromObject(card),
                fragment: cardDetails,
                fragmentName: 'cardDetails',
              });

              const newCardElementConfiguration =
                data.cardElementConfigurationCreate.card_element_configuration;

              const updatedCard = produce(cachedCard, draft => {
                draft.card_side_groups.records[0].card_sides.records
                  .find(x => x.id === cardSide.id)
                  .card_element_configurations.push(
                    newCardElementConfiguration,
                  );
              });

              cache.writeFragment({
                id: defaultDataIdFromObject(card),
                fragment: cardDetails,
                fragmentName: 'cardDetails',
                data: updatedCard,
              });
            },
          });
        } catch {
          //  Ignore the error caught. Worst case scenario config is not set up.
        }
      }

      await cardTemplateUpdate({
        variables: {
          input: {
            card_template_id: cardTemplate.id,
            card_element_ids: [
              ...cardTemplate.card_elements.map(({ id }) => id),
              cardElement.id,
            ],
          },
        },
      });

      if (cardElementConfiguration?.photo_type !== PHOTO_TYPES.FOREGROUND) {
        setSelectedCardElementId(cardElement.id);
      }
    } catch (error) {
      openAlert({
        message: 'An unexpected error occurred.',
        info: getErrorString(error),
      });
    }
  };

  return onCardElementCreate;
};

export default useCardElementCreate;
